import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

const ROLES = ["admin", "manager", "team_lead", "hr", "ceo", "qa", "coder"];

const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  team_lead: "Team Lead",
  hr: "HR",
  ceo: "CEO",
  qa: "QA",
  coder: "Coder",
};

const EMPTY_FORM = {
  email: "",
  name: "",
  role: "coder",
  employee_id: "",
  reporting_team_lead_id: "",
  reporting_manager_id: "",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [reporting, setReporting] = useState({ team_leads: [], managers: [] });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    if (["admin", "manager", "team_lead"].includes(user?.role)) {
      api.get("/users/reporting-options").then((r) => setReporting(r.data)).catch(() => {});
    }
  }, [user]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/auth/register", form);
      toast.success("User created — default password: Solemnity@123 (user must change on first login)");
      setForm(EMPTY_FORM);
      load();
      // Refresh reporting options if a new team_lead/manager was added
      api.get("/users/reporting-options").then((r) => setReporting(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const resetPassword = async (id, email) => {
    if (!confirm(`Reset password for ${email}? It will be set back to Solemnity@123 and the user must change it at next login.`)) return;
    try {
      const { data } = await api.post(`/users/${id}/reset-password`);
      toast.success(`Password reset — ${data.email} can now sign in with ${data.default_password}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const isAdmin = user?.role === "admin";
  const canCreate = ["admin", "manager", "team_lead"].includes(user?.role);
  const canResetPassword = ["admin", "manager", "team_lead"].includes(user?.role);
  // Managers and Team Leads cannot create new Admins
  const assignableRoles = isAdmin ? ROLES : ROLES.filter((r) => r !== "admin");
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="users-page">
      <PageHeader title="Users" subtitle="Manage coders, QAs, team leads, managers and admins." />

      {canCreate && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2"><UserPlus size={18}/> Create user</h3>
          <div className="mb-4 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900" data-testid="default-password-banner">
            New users are created with the default password <strong className="font-mono">Solemnity@123</strong>. They will be prompted to set a new password on their first login.
          </div>
          <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="create-user-form">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Name</label>
              <input required value={form.name} onChange={(e) => setF("name", e.target.value)}
                     data-testid="user-name-input" placeholder="Full name"
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Employee ID</label>
              <input value={form.employee_id} onChange={(e) => setF("employee_id", e.target.value)}
                     data-testid="user-empid-input" placeholder="e.g. EMP-1024"
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setF("email", e.target.value)}
                     data-testid="user-email-input" placeholder="name@solemnity.com"
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Role</label>
              <select value={form.role} onChange={(e) => setF("role", e.target.value)}
                      data-testid="user-role-select"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
                {assignableRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Reporting Team Lead</label>
              <select value={form.reporting_team_lead_id} onChange={(e) => setF("reporting_team_lead_id", e.target.value)}
                      data-testid="user-team-lead-select"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
                <option value="">— None —</option>
                {reporting.team_leads.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Reporting Manager</label>
              <select value={form.reporting_manager_id} onChange={(e) => setF("reporting_manager_id", e.target.value)}
                      data-testid="user-manager-select"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-500 outline-none">
                <option value="">— None —</option>
                {reporting.managers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
              <button disabled={creating} data-testid="create-user-btn"
                      className="bg-sky-700 text-white rounded-lg px-5 py-2 text-sm font-medium hover:bg-sky-600 disabled:opacity-60">
                {creating ? "Creating…" : "Add user"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Emp ID</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Reporting Team Lead</th>
              <th className="text-left px-4 py-3">Reporting Manager</th>
              {(canResetPassword || isAdmin) && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => {
              const isSelf = u.email === user.email;
              const canResetThis = canResetPassword && !isSelf && !(u.role === "admin" && !isAdmin);
              return (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700 font-mono text-xs">{u.employee_id || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium border border-slate-200 bg-slate-50 text-slate-700">{ROLE_LABELS[u.role] || u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.reporting_team_lead_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{u.reporting_manager_name || "—"}</td>
                  {(canResetPassword || isAdmin) && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        {canResetThis && (
                          <button onClick={() => resetPassword(u.id, u.email)}
                                  data-testid={`reset-password-${u.email}`}
                                  className="text-sky-700 text-sm hover:text-sky-900">Reset password</button>
                        )}
                        {isAdmin && !isSelf && (
                          <button onClick={() => remove(u.id)} className="text-red-600 text-sm hover:text-red-800" data-testid={`delete-user-${u.email}`}>Delete</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
